import { Redirect, useHistory } from "react-router-dom";
import { useState } from "react";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import { liveActiveTurn } from "../../../../api/src/shared/domain/gpt-turn";
import { legacyEvaluation } from "../../domains/gpt/stores/annotationSheetStore";
import { z } from "zod";
import { useChatScrollAnchor } from "../../domains/gpt/hooks/useChatScrollAnchor";
import { useChatKeyboardScroll } from "../../domains/gpt/hooks/useChatKeyboardScroll";
import { useLocationState } from "../../domains/app/hooks/useLocationState";
import { useHandedQuestionOwnership } from "../../domains/gpt/hooks/useHandedQuestionOwnership";
import { cn } from "../../lib/utils";
import { displayToast } from "../../components/app/AppToast";
import { BackButton } from "../../components/ui/BackButton";
import { GptConversationBreadcrumb } from "../../components/gpt/GptConversationBreadcrumb";
import { askedFromSchema } from "../../domains/gpt/askedFrom";
import { Button } from "../../components/ui/Button";
import { IconRefresh } from "@tabler/icons-react";
import { ConversationMenu } from "../../components/gpt/ConversationMenu";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import Loader from "../../components/Loader";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import {
  apiClient,
  clearApiQuery,
  useApiQuery,
  useApiRevalidate,
  type Api,
} from "../../services/api";
import { EvidenceChatSurface } from "./EvidenceChatSurface";
import {
  useGptChat,
  uiMessageText,
  toUIMessages,
  dropTurnToRetry,
  afterLostTurn,
  endingAnswerOf,
  questionOriginSchema,
  type GeneralChatUIMessage,
  type QuestionOrigin,
  type RouterPrediction,
  type RouterRedispatch,
} from "../../domains/gpt/hooks/useGptChat";
import {
  useGptMode,
  gptModeSchema,
  type GptMode,
} from "../../domains/gpt/stores/modeStore";
import {
  useEvidenceGptChat,
  type EvidenceGraphView,
  type EvidenceSourceView,
} from "../../domains/gpt/hooks/useEvidenceGptChat";
import { EvidenceCitationSheet } from "../../components/gpt/evidence/EvidenceCitationSheet";
import type {
  GptSearchDoc,
  GptSearchPubmedDoc,
} from "../../components/gpt/chat/GptSearchTurn";
import { useConversationStreamRecovery } from "../../domains/gpt/hooks/useGptStreamRecovery";
import { AcquisitionSuggestionCard } from "../../components/gpt/chat/AcquisitionSuggestionCard";
import { GptConversation } from "../../components/gpt/chat/GptConversation";
import type { GptAnswerForm } from "../../components/gpt/chat/GPTConversationTabs";
import { gptBackground } from "../../components/gpt/layout";
import {
  GPTHistoryButton,
  gptHistoryArrivalSchema,
  GPTHistoryPanel,
  GPTHistoryScrim,
  useGptHistoryReveal,
  type GptHistoryReveal,
} from "../../components/gpt/GPTHistoryPanel";
import {
  currentClient,
  isDesktopShell,
} from "../../common/hooks/useIsDesktopView";
import { OnboardingGettingStarted } from "../../components/gpt/chat/OnboardingGettingStarted";
import { ProfilingConversation } from "../../pages/onboarding/steps/ProfilingConversation";
import { resetPreferenceConversation } from "../../domains/auth/stores/onboardingConversationStore";
import {
  getCurrentUserData,
  replaceCurrentUserData,
  useCurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { useSingleFlightAction } from "../../hooks/useSingleFlightAction";
import { useChatSuggestion } from "../../domains/gpt/hooks/useChatSuggestion";
import {
  hasFinishedAnswer,
  useComposerBand,
} from "../../domains/gpt/hooks/useComposerBand";
import { conversationIdEngine } from "../../domains/assistant/conversationEngine";
import { useConversationEngine } from "../../domains/assistant/useConversationEngine";
import { AssistantConversationPage } from "./AssistantConversationPage";
import { LegacyContinuationPage } from "./LegacyContinuationPage";

// Where a conversation goes when it stops being readable — the user pressed
// Back, or deleted it from either the header menu or its row in the drawer. The
// landing, with the history REVEALED, because that list is what the conversation
// was picked from. Stated once: the page leaves for it from three places, and
// three copies of a navigation are three chances for one to drift.
const HISTORY_ARRIVAL_REVEALING = { historyArrival: "revealing" } as const;
import { useFollowUps } from "../../domains/gpt/hooks/useFollowUps";
import { useCopyAnswer } from "../../domains/gpt/hooks/useCopyAnswer";
import {
  CONVERSATION_SHARE_FLAG,
  useShareConversation,
  useUnshareConversation,
} from "../../domains/gpt/hooks/useShareConversation";
import {
  useDeleteConversation,
  useRenameConversation,
} from "../../domains/gpt/hooks/useManageConversation";
import { useChatAttachments } from "../../domains/gpt/hooks/useChatAttachments";
import { useChatReferences } from "../../domains/gpt/hooks/useChatReferences";
import { useEnsureGptConversation } from "../../domains/gpt/hooks/useEnsureGptConversation";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { seedId } from "../../domains/gpt/answer";
import {
  messageText,
  type ConversationMessage,
} from "../../domains/gpt/transcript";
import {
  gaEventGptInteraction,
  gaEventSearchGpt,
} from "../../tools/analytics/domains/gpt.analytics";
import { gaEventFeedTasteExplored } from "../../tools/analytics/domains/onboarding.analytics";
import { useTranslation } from "react-i18next";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { GptConversationHeader } from "../../components/gpt/GptConversationHeader";
import { useKeyboardManaged } from "../../hooks/use-keyboard-managed";

// A turn already being written server-side when this page reads the
// conversation, described so the recovery can recognise it once it lands (the
// question and the answer the transcript ends with, see `SentTurn`), plus the
// marker's timestamp — the identity that tells one in-flight turn from the next
// one on the same question.
type PendingTurn = {
  kind: "ask" | "regenerate";
  question: string;
  endedWith: string | null;
  startedAt: string;
};

// Where a blocking (Evidence / Search) turn lands: see `transcriptUnder`.
type BlockingTurnPlacement =
  | { kind: "append"; onto: GeneralChatUIMessage[] }
  | { kind: "replaceTrailingTurn" };

// Client deadline for the blocking Search turn (a MeSH resolve + retrieval), over
// the request layer's 60 s default so a slow connection doesn't cancel it.
const SEARCH_TURN_TIMEOUT_MS = 120_000;

// Coerce an additive hydrate field to `[]` when a rolling-deploy API build omits
// it. The parameter is typed `| undefined` on purpose — the query's response type
// claims the field is always present, but at runtime an older API can leave it
// out, and `for (const x of undefined)` crashes the page. Passing the typed value
// is fine (it's assignable), and the `undefined` check stays meaningful.
function orEmptyArray<T>(value: readonly T[] | undefined): readonly T[] {
  return value === undefined ? [] : value;
}

// What the surface that opened this page handed over: streamed content from the
// shared-conversation fork so the page renders instantly after its end-of-stream
// redirect (question+answer), the question the JuisciGPT landing composed for
// this page to ASK (`pending`), and the onboarding `revealOpener` flag
// (typewriter the persisted opener on first landing). All fields default for
// cold entries (history list, deep link, hard reload).
const locationStateSchema = z.object({
  initialQuestionOwnerUid: z.string().nullable().default(null),
  // How the history drawer enters (see `gptHistoryArrivalSchema`). A row
  // picked in an open drawer sends `dismissing`, so this conversation paints
  // under it and plays it closed instead of replacing it in one cut.
  historyArrival: gptHistoryArrivalSchema.default("closed"),
  question: z.string().nullable().default(null),
  answer: z.string().nullable().default(null),
  // The JuisciGPT landing composes the question and this page asks it: the
  // conversation exists (the landing minted it), the turn does not yet. One
  // object rather than two parallel fields, so "a question with no origin" is
  // unrepresentable. Live-only, like every router-state field: a hard reload
  // drops it, which is exactly right — the turn it described is either already
  // in the transcript or was never sent.
  pending: z
    .object({
      question: z.string().min(1),
      origin: questionOriginSchema,
      // The mode the user had selected WHEN they submitted on the landing
      // (docs/gpt-mode-router.md). Carried so the turn is sent + persisted in the
      // submitted mode, not whatever the global picker holds by the time this
      // page mounts (the user may have changed it during the async create).
      // `null` on a legacy handoff → fall back to the current picker.
      selectedMode: gptModeSchema.nullable().default(null),
    })
    .nullable()
    .default(null),
  revealOpener: z.boolean().default(false),
  // The page the question was asked from, which the breadcrumb leads back to.
  askedFrom: askedFromSchema,
});

// Dispatcher: an existing conversation is rendered by its stored ORIGIN, not by
// the current flag — so a user with EXPERIMENTAL on still reads their old
// general threads, and Evidence threads always render with the Evidence agent.
// A brand-new thread's agent is chosen on the new-chat page (by the flag). The
// origin fetch is the SAME cached query the streaming page reads, so branching
// costs no extra request.
export default function GPTConversationPage({
  id,
  roomId,
}: {
  id: string;
  roomId: string | null;
}): React.ReactElement {
  const history = useHistory();
  const { gptNewChatPageLink, gptConversationPageLink } = usePageLinks(roomId);
  // A `?new` conversation was just minted by the new-chat page (or a shared fork)
  // — always a `juisci_gpt_page` streaming thread, never a legacy
  // `evidence_gpt_page` one. Render the streaming surface IMMEDIATELY: it shows
  // the just-submitted question (the `pending` hand-off) at once, instead of a
  // full-page loader blocking on the origin fetch for a couple of seconds.
  const isNewConversation =
    new URLSearchParams(history.location.search).get("new") !== null;
  // The id says which engine owns the conversation.
  const engine = conversationIdEngine(id);
  const originQuery = useApiQuery(
    "/webapp/private/conversations/:id",
    id === "" || engine === "assistant" ? false : { param: { id } }
  );
  // Under the assistant engine, a general legacy conversation continues there.
  const engineFlag = useConversationEngine();
  const continuable =
    originQuery.state === "success" &&
    continuesOnAssistant(originQuery.body.origin)
      ? originQuery.body
      : null;
  const continuedAs =
    continuable !== null && engineFlag === "assistant"
      ? continuable.continuedAs
      : null;
  // `?new` covers the first send only: an old link to an answered conversation is routed like any other.
  const isFirstSend =
    isNewConversation &&
    (continuable === null || continuable.messages.length === 0);
  const { historyArrival } = useLocationState(locationStateSchema);
  // "Customize my feed" takes over the page (no modal): the preference
  // conversation swaps in below the chat's header, and finishing it returns to
  // the chat. Owned HERE, one level above the surface that renders it, because
  // the drawer's swipe gesture is armed here and that takeover has no drawer.
  const [customizing, setCustomizing] = useState(false);
  // The drawer is owned at the TOP of this page, above every loading branch
  // below, rather than inside the chat surface. An arrival animation has to run
  // from the surface's first paint (see `gptHistoryArrivalSchema`), and a
  // conversation opened from a drawer row paints a loader first — a drawer
  // mounted after that loader resolves would flash open and shut a beat late.
  // The desktop sidebar lists the conversations, so the drawer is mobile only,
  // and on desktop nothing may open it: a touchscreen laptop (or a browser
  // emulating touch) would otherwise swipe the page aside onto an empty panel.
  const hasHistoryDrawer = !isDesktopShell();
  const historyReveal = useGptHistoryReveal({
    arrival: hasHistoryDrawer ? historyArrival : "closed",
    swipeEnabled: hasHistoryDrawer && !customizing,
  });

  // The legacy Evidence surface brings its OWN drawer (a different list, over a
  // different pair of feeds), so it stands outside this page's card rather than
  // inside it, and a row that opens one plays no arrival animation.
  //
  // key={id}: switching between two evidence threads (e.g. from the history
  // drawer) changes only the route param, not the mounted component — without
  // a per-id key the surface keeps the first thread's state (its hook seeds
  // conversationId once). Re-keying remounts it fresh on each thread.
  if (
    !isNewConversation &&
    originQuery.state === "success" &&
    originQuery.body.origin === "evidence_gpt_page"
  )
    return <EvidenceChatSurface key={id} conversationId={id} roomId={roomId} />;

  return (
    <>
      {hasHistoryDrawer ? (
        <GPTHistoryPanel
          isOpen={historyReveal.isOpen}
          roomId={roomId}
          onNewConversation={() => {
            history.push(gptNewChatPageLink(), {
              historyArrival: "dismissing",
            });
          }}
          // Deleting the thread this page is showing leaves it on a conversation
          // that no longer exists, so it goes where Back goes.
          onConversationDeleted={(deletedIds) => {
            if (!deletedIds.includes(id)) return;
            history.replace(gptNewChatPageLink(), HISTORY_ARRIVAL_REVEALING);
          }}
        />
      ) : null}
      {/* The card the drawer pushes aside — the box `ScrollableLayout`'s own
          container used to be, lifted one level so it wraps the loading
          branches too. Viewport-sized in normal flow (the reveal's class is
          `relative`, and this is the page's root), which is what it already
          resolved to inside the layout. Each branch below is `fixed` and lands
          against THIS box rather than the viewport, because the reveal's
          transform makes it their containing block — the same reason the
          composer inside the chat belongs to the card. */}
      <div
        className={cn("h-dvh w-full", historyReveal.containerClassName)}
        style={historyReveal.containerStyle}
      >
        {engine === "assistant" ? (
          <AssistantConversationPage
            key={id}
            id={id}
            roomId={roomId}
            historyReveal={historyReveal}
          />
        ) : continuedAs !== null ? (
          // Before `?new`: the URL of the conversation's first question still leads to its continuation.
          <Redirect to={gptConversationPageLink(continuedAs)} />
        ) : // A `?new` conversation renders the streaming surface at once (see
        // above). Otherwise hold a neutral loader until the origin is known,
        // rather than mounting the general streaming chat speculatively — an
        // Evidence thread would flash the general agent's chrome, and a
        // just-answered turn would blink out, before switching. The streaming
        // page shares this cached fetch, so the general path pays no extra
        // request and no second loader.
        // A turn the legacy engine is writing lands here too, where its page recovers it.
        isFirstSend ||
          (originQuery.state !== "pending" &&
            (continuable === null ||
              engineFlag === "legacy" ||
              liveActiveTurn(continuable, Date.now()) !== null)) ? (
          <GPTConversationPageStreaming
            key={id}
            id={id}
            roomId={roomId}
            historyReveal={historyReveal}
            customizing={customizing}
            onCustomizingChange={setCustomizing}
          />
        ) : continuable === null || engineFlag === "pending" ? (
          <Loader expanded />
        ) : (
          // key={id}: its chat and its continuation id belong to one conversation.
          <LegacyContinuationPage
            key={id}
            id={id}
            roomId={roomId}
            historyReveal={historyReveal}
            conversation={continuable}
          />
        )}
        {hasHistoryDrawer ? (
          <GPTHistoryScrim
            isOpen={historyReveal.isOpen}
            onClose={historyReveal.close}
          />
        ) : null}
      </div>
    </>
  );
}

// The origins the continue route accepts: the dashboard still writes its conversations, and an onboarding opener has no question to copy.
function continuesOnAssistant(
  origin: Api["get"]["/webapp/private/conversations/:id"]["output"]["body"]["origin"]
): boolean {
  switch (origin) {
    case "juisci_gpt_page":
      return true;
    case "dashboard":
    case "onboarding":
    case "article_page":
    case "evidence_gpt_page":
    case "admin_profile":
      return false;
    default:
      throw new Error(origin satisfies never);
  }
}

function GPTConversationPageStreaming({
  id,
  roomId,
  historyReveal,
  customizing,
  onCustomizingChange,
}: {
  id: string;
  roomId: string | null;
  /** Owned by the page above, which mounts the drawer over every branch here. */
  historyReveal: GptHistoryReveal;
  customizing: boolean;
  onCustomizingChange: (customizing: boolean) => void;
}): React.ReactElement {
  useKeyboardManaged();
  const { t } = useTranslation();
  const { gptNewChatPageLink } = usePageLinks(roomId);
  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const isRoomLoading = roomQuery.state === "pending" && roomQuery.isFetching;
  const navigationState = useLocationState(locationStateSchema);
  const { question, answer, revealOpener, initialQuestionOwnerUid } =
    navigationState;
  const history = useHistory();
  const questionOwnership = useHandedQuestionOwnership(
    initialQuestionOwnerUid,
    navigationState.pending
  );
  const handedPending = questionOwnership.question;
  const pending = questionOwnership.isOwner() ? handedPending : null;
  // The shared-fork hand-off (instant render of the just-streamed turn) needs
  // both question + answer; absent on cold entries and on the onboarding launch.
  const handoff =
    question !== null && answer !== null ? { question, answer } : null;
  const conversationQuery = useApiQuery(
    "/webapp/private/conversations/:id",
    id === "" ? false : { param: { id } }
  );

  const isHandedQuestionPersisted =
    initialQuestionOwnerUid !== null &&
    handedPending !== null &&
    conversationQuery.state === "success" &&
    conversationQuery.body.messages.length > 0;
  useEffectOnce(() => {
    if (!isHandedQuestionPersisted || !questionOwnership.isOwner()) return;
    const { pathname, search, hash } = history.location;
    history.replace({
      pathname,
      search,
      hash,
      state: {
        ...navigationState,
        pending: null,
        initialQuestionOwnerUid: null,
      },
    });
  }, [isHandedQuestionPersisted]);

  if (roomId !== null && isRoomLoading) return <Loader expanded />;

  const dbMessages =
    conversationQuery.state === "success"
      ? conversationQuery.body.messages
      : [];
  // Evidence turns' rich graphs (docs/gpt-mode-router.md): the unified general
  // hydrate returns them as an additive side-car keyed by turnIndex. Rendered
  // inline in the transcript alongside general turns.
  //
  // `orEmptyArray` guards a ROLLING-DEPLOY / version skew: these are additive
  // hydrate fields, so a webapp built against the newer contract can, mid-deploy,
  // hit an API build that doesn't return them yet — the field is `undefined` at
  // runtime even though the type says it's an array. Defaulting to `[]` keeps the
  // transcript rendering (as general turns) instead of crashing the whole page on
  // `for (const turn of undefined)`.
  const evidenceAnswers = orEmptyArray(
    conversationQuery.state === "success"
      ? conversationQuery.body.evidenceAnswers
      : []
  );
  // Search turns (find_publication, docs/gpt-mode-router.md): the unified hydrate
  // reconstructs each persisted `searchPublications` turn (framing + refinements
  // + the same article cards the live /search returned), keyed by turnIndex.
  // Rendered inline as result rows — so a reload shows the rows, not a plain
  // answer. Same rolling-deploy guard as `evidenceAnswers` above.
  const searchTurns = orEmptyArray(
    conversationQuery.state === "success"
      ? conversationQuery.body.searchTurns
      : []
  );
  // This reader's own verdicts on this conversation (empty for everyone outside
  // the annotation pilot). Same rolling-deploy guard as the two side-cars above:
  // an additive hydrate field reads `undefined` at runtime when a newer webapp
  // build meets an API that does not return it yet.
  const myAnnotations = orEmptyArray(
    conversationQuery.state === "success"
      ? conversationQuery.body.myAnnotations
      : []
  );
  const seededTurn: GeneralChatUIMessage[] =
    handoff === null
      ? []
      : [
          {
            id: seedId("user", 0),
            role: "user",
            parts: [{ type: "text", text: handoff.question }],
          },
          {
            id: seedId("assistant", 0),
            role: "assistant",
            parts: [{ type: "text", text: handoff.answer }],
          },
        ];
  // No proposal is ever injected here: the first turn now STREAMS on this page,
  // so its `data-suggestion` part lands in `chat.messages` where
  // `useChatSuggestion` reads it. (It used to be handed over from the new-chat
  // landing, which streamed the first turn and then navigated away from it.)
  const initialMessages: GeneralChatUIMessage[] =
    dbMessages.length > 0 ? toUIMessages(dbMessages) : seededTurn;

  // Server-side in-flight recovery (app backgrounded / webview killed mid-answer):
  // the conversation carries `activeTurnStartedAt` + the pending `activeTurnQuestion`
  // while a turn is being written server-side. On a COLD entry (no live hand-off)
  // we show that question with a working state and poll for the answer.
  //
  // A set-and-recent marker IS the in-flight signal — no transcript-content check
  // needed. The server clears it atomically with landing the answer, so we never
  // see "answer present + marker set"; and because it is content-free, a
  // regenerate or a repeated question (whose transcript still ends with the OLD
  // answer during flight) recovers correctly rather than reading as settled.
  // Ignored when: a hand-off already carries the turn, the question handed over
  // by the landing is about to be asked here (its own send owns the turn), or
  // the marker is STALE (`liveActiveTurn`: older than the stream ceiling → the
  // server died mid-turn, nothing coming).
  //
  // Read on EVERY render, not once: the body this page mounts on may be the
  // persisted cache, a turn behind the server. `Conversation` re-reads it as the
  // query refetches (see its adoption effect).
  const activeTurn =
    conversationQuery.state === "success"
      ? liveActiveTurn(conversationQuery.body, Date.now())
      : null;
  const lastDbMessage = dbMessages.at(-1);
  // The answer the conversation currently ENDS with — during flight this is the
  // PREVIOUS turn's answer (the server clears the marker in the same write that
  // lands the new one, so a set marker guarantees the new answer is NOT here
  // yet). It is the landmark half the recovery probe compares against, and it is
  // exactly what tells a regenerate's NEW answer from the OLD one it replaces.
  const endedWith =
    lastDbMessage !== undefined && lastDbMessage.role === "assistant"
      ? messageText(lastDbMessage)
      : null;
  const lastDbQuestion = dbMessages.findLast(
    (message) => message.role === "user"
  );
  const pendingTurn: PendingTurn | null =
    handoff !== null || pending !== null || activeTurn === null
      ? null
      : {
          // An ask is persisted with its answer, so a transcript that already
          // ends on the in-flight question and an answer is being regenerated.
          // A question asked twice in a row reads as a Regenerate too: its retry
          // then replaces the first answer instead of appending a copy.
          kind:
            endedWith !== null &&
            lastDbQuestion !== undefined &&
            messageText(lastDbQuestion).trim() === activeTurn.question.trim()
              ? "regenerate"
              : "ask",
          question: activeTurn.question,
          endedWith,
          startedAt: activeTurn.startedAt,
        };

  // Seed the pending question as the trailing in-flight turn so it renders while
  // recovery polls (the inner arms `onTurnInFlight`). Not a Regenerate's: its
  // question already ends the transcript, and a copy after its answer would be
  // one question more than the server holds. Mutually exclusive with the
  // hand-off seed above (pendingTurn requires `handoff === null`). Its id is keyed
  // PAST the existing messages' indices — `toUIMessage` ids each DB message
  // `seed-<role>-<index>`, so a fixed `seed-user-0` would collide with the first
  // user message on a non-empty conversation.
  const messagesWithPending: GeneralChatUIMessage[] =
    pendingTurn === null || pendingTurn.kind === "regenerate"
      ? initialMessages
      : [
          ...initialMessages,
          {
            id: seedId("user", initialMessages.length),
            role: "user",
            parts: [{ type: "text", text: pendingTurn.question }],
          },
        ];

  // Typewriter the onboarding opener on its first landing: a single persisted
  // assistant message + the revealOpener flag. Its UI id is the one to reveal.
  const opener =
    revealOpener && initialMessages.length === 1
      ? initialMessages[0]
      : undefined;
  const revealAssistantId =
    opener !== undefined && opener.role === "assistant" ? opener.id : null;

  // A COLD entry — no hand-off, no question to ask — has nothing to show until
  // the transcript is here: a full-page loader while it loads, and the failure
  // with a way to ask again when it does not. A hand-off renders its seeded turn
  // instantly, and a question handed over by the landing must reach `submit` NOW
  // rather than wait a round-trip on a transcript we already know is empty — in
  // both cases a Loader would read as a reload.
  //
  // The error branch is what used to be a "blank page": the read failing —
  // typically the auth token not being readable yet in the seconds after the
  // native shell reloaded the webview — fell through and mounted the chat on an
  // EMPTY transcript, a page that looked like a conversation with nothing in it,
  // over a conversation that had content.
  const isColdEntry = handoff === null && pending === null;
  if (
    isColdEntry &&
    (conversationQuery.state === "pending" ||
      conversationQuery.state === "error")
  ) {
    return (
      <ScrollableLayout
        containerStyle={{ background: gptBackground() }}
        background={gptBackground()}
        headerClassName="bg-white/90 backdrop-blur-md"
        header={
          <GptConversationHeader
            title={null}
            onBack={null}
            visualAbstracts={null}
            newConversationTo={gptNewChatPageLink()}
            // Nothing loaded yet: there is no conversation to act on.
            actions={null}
          />
        }
      >
        {conversationQuery.state === "pending" ? (
          <Loader expanded />
        ) : (
          <div className="mx-5 mt-8 flex flex-col items-start gap-2">
            <p className="my-0 text-sm text-secondary-raspberry-1">
              {t("Something went wrong. Please try again.")}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              leftIcon={<IconRefresh className="size-4" stroke={1.5} />}
              onClick={() => {
                void conversationQuery.refetch();
              }}
            >
              {t("Retry")}
            </Button>
          </div>
        )}
      </ScrollableLayout>
    );
  }

  // The persisted transcript, once it has arrived, or null. Only the HAND-OFF
  // path can mount before it resolves (with a bare-text seed), so only that path
  // has anything to adopt; `Conversation` decides, and does it with
  // `setMessages` rather than by being re-keyed. See its adoption effect for why
  // a remount is not an option here.
  const hydratedTranscript: GeneralChatUIMessage[] | null =
    dbMessages.length > 0 ? toUIMessages(dbMessages) : null;

  // Onboarding conversations (B2C users land on one right after onboarding)
  // carry the getting-started actions while the opener is the only message.
  const isOnboardingConversation =
    conversationQuery.state === "success" &&
    conversationQuery.body.origin === "onboarding";

  // Named server-side from the first question, so it is null on a conversation
  // whose first turn has not landed yet (the new-chat hand-off) — the header
  // simply shows no title until the transcript query catches up.
  const title =
    conversationQuery.state === "success" ? conversationQuery.body.title : null;
  // False until the transcript query lands: a conversation just created has no link.
  const isShared =
    conversationQuery.state === "success" && conversationQuery.body.isShared;

  return (
    <Conversation
      key={id}
      id={id}
      roomId={roomId}
      historyReveal={historyReveal}
      customizing={customizing}
      onCustomizingChange={onCustomizingChange}
      title={title}
      isShared={isShared}
      initialMessages={messagesWithPending}
      hydratedTranscript={hydratedTranscript}
      hydratedEvidenceAnswers={evidenceAnswers}
      hydratedSearchTurns={searchTurns}
      myAnnotations={myAnnotations}
      seeded={handoff !== null}
      pending={pending}
      isPendingQuestionOwner={questionOwnership.isOwner}
      pendingTurn={pendingTurn}
      revealAssistantId={revealAssistantId}
      isOnboardingConversation={isOnboardingConversation}
    />
  );
}

function Conversation({
  id,
  roomId,
  historyReveal,
  customizing,
  onCustomizingChange,
  title,
  isShared,
  initialMessages,
  hydratedTranscript,
  hydratedEvidenceAnswers,
  hydratedSearchTurns,
  myAnnotations,
  seeded,
  pending,
  isPendingQuestionOwner,
  pendingTurn,
  revealAssistantId,
  isOnboardingConversation,
}: {
  id: string;
  roomId: string | null;
  /** Owned by the page above, which mounts the drawer over every branch here. */
  historyReveal: GptHistoryReveal;
  /** True while "Customize my feed" has taken the page over. */
  customizing: boolean;
  onCustomizingChange: (customizing: boolean) => void;
  /** Server-generated conversation title; null until the first turn is named. */
  title: string | null;
  /** A public link shows the conversation: the menu offers Stop sharing. */
  isShared: boolean;
  initialMessages: GeneralChatUIMessage[];
  // The persisted transcript once the conversation query has resolved, or null
  // while it is still in flight. Distinct from `initialMessages`, which is read
  // ONCE (it seeds the chat at mount): this one is allowed to arrive later, and
  // the adoption effect below decides whether replacing the live transcript with
  // it is safe.
  hydratedTranscript: GeneralChatUIMessage[] | null;
  // Past Evidence turns' graphs from the unified hydrate side-car, keyed by
  // turnIndex (docs/gpt-mode-router.md). A wider graph is assignable to the
  // renderer's `EvidenceGraphView` Pick.
  hydratedEvidenceAnswers: ReadonlyArray<{
    turnIndex: number;
    question: string;
    answer: EvidenceGraphView;
  }>;
  // Past Search turns from the unified hydrate, keyed by turnIndex
  // (docs/gpt-mode-router.md): framing + refinement chips + the same article
  // cards the live /search returned, so a reloaded Search turn renders as rows.
  hydratedSearchTurns: ReadonlyArray<{
    turnIndex: number;
    framing: string;
    refinements: string[];
    docs: GptSearchDoc[];
    pubmedDocs: GptSearchPubmedDoc[];
  }>;
  // This reader's own verdicts, keyed by turnIndex — what makes the action bar say
  // "revise" and what prefills the sheet. Inferred from the conversation read
  // rather than redeclared, so the rubric shape has exactly one definition.
  myAnnotations: ReadonlyArray<
    Api["get"]["/webapp/private/conversations/:id"]["output"]["body"]["myAnnotations"][number]
  >;
  seeded: boolean;
  isPendingQuestionOwner: () => boolean;
  // The question the JuisciGPT landing composed and handed over to be ASKED
  // here, or null. Sent once on mount — the conversation is this page's from the
  // first token on, which is what puts its header on screen while the answer is
  // still being written.
  pending: {
    question: string;
    origin: QuestionOrigin;
    selectedMode: GptMode | null;
  } | null;
  // A turn in flight server-side as of the LATEST conversation read (recovered
  // from its `activeTurnStartedAt`), or null. When set at mount, the last
  // `initialMessages` entry is its pending question; when it appears later (a
  // fresher read than the one this page mounted on), the adoption effect appends
  // the question itself. Either way recovery is armed to poll for the answer.
  pendingTurn: PendingTurn | null;
  revealAssistantId: string | null;
  isOnboardingConversation: boolean;
}): React.ReactElement {
  const { t } = useTranslation();
  const history = useHistory();
  const { activeLang } = useActiveLang();
  const { currentUser } = useCurrentUser();
  const { gptNewChatPageLink, articleLink } = usePageLinks(roomId);
  // Conversation sharing has its own gate: off → the Share action stays a
  // "coming soon" stub (no link minted), on → it mints + shares.
  const isShareEnabled = useFeatureIsOn(CONVERSATION_SHARE_FLAG);
  // The PubMed section of a Search turn is behind EXPERIMENTAL too: off, the
  // Search route does not query PubMed at all. Read reactively and closed over by
  // `runSearchTurn`. A send typed on this page fires well after the flags load.
  // The question handed over by the landing page is sent at mount: if the flags
  // have not loaded by then, that one turn is saved without a PubMed section.
  const isPubmedSectionEnabled = useFeatureIsOn("EXPERIMENTAL");
  const [mode] = useGptMode();
  // Open an existing conversation on its latest turn, but never auto-follow the
  // streamed answer (the jump button covers the manual case).
  const { scrollRef, isAtBottom, scrollToBottom, onScroll } =
    useChatScrollAnchor({ initialToBottom: true });
  // When the keyboard opens, pad the scroller and pin it to the bottom so the
  // answer's tail clears the raised composer instead of hiding behind it.
  const { paddingBottom: keyboardPaddingBottom } =
    useChatKeyboardScroll(scrollToBottom);

  const searchParams = new URLSearchParams(history.location.search);
  const isNewConversation = searchParams.get("new") !== null;

  // Follow-ups — declared before the chat so its `onTurnFinish` can refresh
  // them.
  const followUps = useFollowUps({
    enabled: true,
    source: { kind: "general" },
  });
  const copyAnswer = useCopyAnswer();
  const shareConversation = useShareConversation();
  const unshareConversation = useUnshareConversation();
  const renameConversation = useRenameConversation();
  const deleteConversation = useDeleteConversation();
  const revalidate = useApiRevalidate();

  // The conversation already exists, so attaching is a plain upload / a plain
  // reference — the coordinator never creates here (target is fixed to `id`),
  // and files/playlists attached in an earlier visit are listed by the hooks'
  // own queries.
  const conversation = useEnsureGptConversation({
    conversationId: id,
    engine: "legacy",
  });
  const attachments = useChatAttachments({ conversation });
  const references = useChatReferences({ conversation });

  // A turn whose connection dropped is still being written server-side, so the
  // page waits for the persisted answer instead of showing an error over it.
  // Declared before the chat because the chat's `onFailure` is what arms it;
  // `adoptRecoveredTurn` runs after render, when `chat` exists.
  const recovery = useConversationStreamRecovery({
    conversationId: id,
    onTurnRecovered: (messages) => {
      adoptRecoveredTurn(messages);
    },
    onTurnLost: () => {
      stopLostTurn();
    },
  });

  const chat = useGptChat({
    transport: { kind: "general", conversationId: id, language: activeLang },
    initialMessages,
    // The turn is now in progress server-side, and the conversation list shows
    // such rows: refetch it so the desktop sidebar lists a new conversation
    // while its first answer is still being written.
    onTurnAccepted: () => {
      void revalidate("/webapp/private/conversations");
    },
    onTurnFinish: (question, answer) => {
      recovery.onTurnSettled();
      followUps.refresh(question, answer);
      // The turn is now persisted server-side: refresh the conversation
      // transcript cache (so a back-navigation re-seeds the full, source-
      // hydrated history) and the history list (lastQuestion / ordering). The
      // on-screen view reads the live `chat.messages`, not these caches, so
      // this never disrupts the current turn.
      void revalidate("/webapp/private/conversations/:id", { param: { id } });
      void revalidate("/webapp/private/conversations");
      // The subject the landing greeting names may have changed. REMOVED, not
      // revalidated: the landing is not mounted now, so a revalidation would
      // only mark the entry stale, and the next landing would paint the old
      // subject for a frame before swapping it. Removed, it waits for the
      // fresh one.
      clearApiQuery("/webapp/private/conversations/latest-topic", {});
    },
    // No toast: `GPTChatError` (rendered in the conversation once the recovery
    // window has closed) is the single feedback surface, and it can tell a REFUSAL
    // (out of credits, with when to come back and the push opt-in) from a real
    // failure by asking the server's typed budget state. A toast here could only
    // guess from the error text — which is what the removed
    // `["daily","limit","reach"]` sniffing did, missing the weekly ceiling and
    // mislabelling other errors.
    onFailure: recovery.onFailure,
    // Auto → Evidence / Search: the general stream routed this turn to a blocking
    // engine. Drop the answerless turn and run it (docs/gpt-mode-router.md).
    onRedispatch: (redispatch) => {
      redispatchToBlockingEngine(redispatch);
    },
  });

  // ── Mode Router: the Evidence engine, coordinated onto the SAME conversation
  // (docs/gpt-mode-router.md). `hydrate: false` — past Evidence turns come from
  // the general hydrate side-car (`hydratedEvidenceAnswers`), not the
  // Evidence-only endpoint (which 409s on this `juisci_gpt_page` origin). This
  // hook is used only to SEND and to hold this session's live turns.
  const evidence = useEvidenceGptChat({
    conversationId: id,
    includeWeb: true,
    language: activeLang,
    hydrate: false,
  });
  // Every Evidence turn's graph, keyed by turnIndex: past (hydrate side-car) +
  // this session's live sends. A turn whose index is here renders as an Evidence
  // turn in the transcript.
  const evidenceGraphByTurnIndex = new Map<number, EvidenceGraphView>();
  for (const turn of hydratedEvidenceAnswers)
    evidenceGraphByTurnIndex.set(turn.turnIndex, turn.answer);
  for (const turn of evidence.turns)
    if (turn.status === "answered")
      evidenceGraphByTurnIndex.set(turn.turnIndex, turn.answer);
  // The forced-Evidence turn whose blocking call is in flight (shown as a working
  // turn), and the open citation source (the sheet), lifted here — this view owns
  // the coordinator state, GptConversation stays presentational.
  // `renderQuestion` is false on an Auto→Evidence redispatch: the answerless
  // general turn already shows the question in the transcript, so the working
  // card renders the thinking loader ALONE (else the question appears twice).
  const [pendingEvidence, setPendingEvidence] = useState<{
    question: string;
    renderQuestion: boolean;
  } | null>(null);
  const [openEvidenceSource, setOpenEvidenceSource] = useState<{
    source: EvidenceSourceView;
    citationNumber: number;
    quotes: readonly string[];
  } | null>(null);

  // ── Mode Router: the Search engine (find_publication — docs/gpt-mode-router.md).
  // A Search turn in flight (shown as a working turn), and this session's
  // answered search turns keyed by turnIndex (rendered inline as the same result
  // rows as /search). Hydrated (reload) search turns are a follow-up.
  // `renderQuestion` is false on an Auto→Search redispatch: the answerless
  // general turn already shows the question, so the working card is the loader
  // alone (same contract as `pendingEvidence`).
  const [pendingSearch, setPendingSearch] = useState<{
    question: string;
    renderQuestion: boolean;
  } | null>(null);
  // Owns the one in-flight BLOCKING turn (Evidence or Search). Its ref closes the
  // same-render event gap synchronously — the `pendingSearch`/`pendingEvidence`
  // state guard in `submit` lags a render, so two `submit` calls in one tick (a
  // double-tapped refinement chip) would both read `null` and fire (cursor:
  // submit guard misses sync double-sends). `run` blocks the second synchronously.
  const blockingDispatch = useSingleFlightAction();
  const [liveSearchTurns, setLiveSearchTurns] = useState<
    {
      turnIndex: number;
      framing: string;
      docs: GptSearchDoc[];
      pubmedDocs: GptSearchPubmedDoc[];
      refinements: string[];
    }[]
  >([]);
  const searchByTurnIndex = new Map<
    number,
    {
      framing: string;
      docs: GptSearchDoc[];
      pubmedDocs: GptSearchPubmedDoc[];
      refinements: string[];
    }
  >();
  // Past turns from the hydrate, then this session's live sends on top (a live
  // turn's overlay covers the window before revalidate refetches the hydrate).
  for (const turn of hydratedSearchTurns)
    searchByTurnIndex.set(turn.turnIndex, {
      framing: turn.framing,
      docs: turn.docs,
      pubmedDocs: turn.pubmedDocs,
      refinements: turn.refinements,
    });
  for (const turn of liveSearchTurns)
    searchByTurnIndex.set(turn.turnIndex, {
      framing: turn.framing,
      docs: turn.docs,
      pubmedDocs: turn.pubmedDocs,
      refinements: turn.refinements,
    });

  // Takes over the composer band when the agent proposes an interest or a Room.
  // Inside a room, room proposals are suppressed (see the hook) — the surface is
  // already a partner space.
  const suggestion = useChatSuggestion(chat, roomId !== null, "legacy");

  const questionCount = chat.messages.filter(
    (message) => message.role === "user"
  ).length;
  const band = useComposerBand(
    suggestion,
    questionCount,
    hasFinishedAnswer(chat.messages, chat.status)
  );

  // Ask the question the landing handed over. Guarded on an EMPTY transcript,
  // which is the honest "this turn has not happened yet", and the guard that
  // survives a remount, should this component ever get one again (the router
  // state still carries the question, so a ref would be fresh and re-ask it;
  // see `.knowledge/router-state-command-replay.md`).
  useEffectOnce(() => {
    if (
      !isPendingQuestionOwner() ||
      pending === null ||
      initialMessages.length > 0
    )
      return;
    submit(
      pending.question,
      pending.origin,
      pending.selectedMode === null ? mode : pending.selectedMode,
      "new"
    );
  });

  // Adopt what the SERVER knows over what this page was seeded with, whenever the
  // server knows more. The seed is permanent as far as `useChat` is concerned (it
  // rebuilds its chat only on an id change), so anything the transcript query
  // learns AFTER mount has to be carried into the chat by hand — and there are
  // three ways it learns more:
  //
  // - The hand-off seed. The shared-fork page streams a turn and navigates here
  //   with its text only, so what is on screen is the last turn in bare prose
  //   while the fork itself holds the whole history with its sources.
  // - A stale cache. Every read is persisted to localStorage, and a `?new`
  //   conversation's first read is an EMPTY transcript with no marker (the marker
  //   is written only once the server's pre-fetch is done). The native shell
  //   reloading the webview — a WebContent process reclaimed while the app was
  //   backgrounded, or fifteen minutes away — then mounts this page on that cached
  //   emptiness: a conversation with nothing in it, over a conversation that is
  //   being answered. That was the "blank page" after locking the phone.
  // - A turn this page never sent. The marker says the server is writing an
  //   answer right now (the app was killed mid-turn and reopened from the history
  //   list): show its question as the last message and hand it to the recovery,
  //   which polls for the answer and adopts it the moment it lands.
  //
  // Done with `setMessages` and NOT by re-keying this component, which is what
  // it used to be: `useChat` keeps its `Chat` in a ref, so a key change remounts
  // and re-seeds from `toUIMessage`, which does not read back the stream's
  // live-only parts. That destroyed the turn's proposal card on the first turn
  // of every conversation. `custom/chat-surface-key-is-identity` now forbids the
  // shape outright.
  //
  // Never while a turn is in flight HERE — streaming, or waiting out a broken
  // connection: the live transcript is then the richer one, or the recovery is
  // about to adopt the answer itself. And on a conversation that is not a
  // hand-off, only when the server's transcript is STRICTLY longer than the one
  // on screen: a turn this page streamed lands in the transcript at the same
  // length, and replacing it would be the same loss by another route.
  useEffectOnce(() => {
    if (chat.status === "submitted" || chat.status === "streaming") return;
    if (recovery.isReconnecting) return;
    if (pendingTurn !== null) {
      // Whose turn does the marker describe? Read it off the transcript on
      // screen: the last question asked here, and whether anything follows it.
      let lastUserIndex = -1;
      for (let index = chat.messages.length - 1; index >= 0; index -= 1) {
        const message = chat.messages[index];
        if (message !== undefined && message.role === "user") {
          lastUserIndex = index;
          break;
        }
      }
      const lastUser =
        lastUserIndex === -1 ? undefined : chat.messages[lastUserIndex];
      const isLastQuestion =
        lastUser !== undefined &&
        uiMessageText(lastUser) === pendingTurn.question;
      const lastMessage = chat.messages.at(-1);
      const answerOnScreen =
        lastMessage !== undefined && lastMessage.role === "assistant"
          ? uiMessageText(lastMessage)
          : null;
      // A marker for the last question on screen, with an answer under it, is
      // one of two things, and the answer tells them apart:
      // - The answer is one the SERVER does not have yet (it differs from what
      //   the persisted transcript ends with): this page has just streamed that
      //   turn and the server is a beat behind, still committing it
      //   (`onTurnFinish` refetches before that write lands). Neither a question
      //   to show again nor a turn to hand to the recovery — adopting the
      //   persisted copy over the live answer would drop its live-only parts,
      //   the proposal card first.
      // - The answer IS the persisted one: the page was seeded from the
      //   transcript, and the marker describes a REGENERATE of that turn in
      //   flight (a cold mount mid-replay). Its new answer lands at the same
      //   transcript length, so only the recovery can bring it in — arm it,
      //   with the old answer as the landmark it has to differ from.
      if (
        isLastQuestion &&
        lastUserIndex < chat.messages.length - 1 &&
        answerOnScreen !== pendingTurn.endedWith
      )
        return;
      // The question is already the last thing on screen (the seed appended
      // it, or a send that never got a byte back): arm without appending.
      if (!isLastQuestion) {
        const base =
          hydratedTranscript === null ? chat.messages : hydratedTranscript;
        chat.setMessages([
          ...base,
          {
            id: seedId("user", base.length),
            role: "user",
            parts: [{ type: "text", text: pendingTurn.question }],
          },
        ]);
        chat.clearError();
      }
      recovery.onTurnInFlight(pendingTurn);
      return;
    }
    if (hydratedTranscript === null) return;
    const hasAskedHere = chat.messages.length > initialMessages.length;
    if (
      seeded ? hasAskedHere : hydratedTranscript.length <= chat.messages.length
    )
      return;
    chat.setMessages(hydratedTranscript);
    chat.clearError();
    // A turn given up on earlier (`isLost`) that has landed after all is
    // history now, not a failure.
    recovery.onTurnSettled();
  }, [
    hydratedTranscript === null ? 0 : hydratedTranscript.length,
    pendingTurn === null ? null : pendingTurn.startedAt,
  ]);

  // Fetch follow-ups for the seeded turn handed over by the shared-conversation
  // fork (the hook's onTurnFinish never fired for it).
  useEffectOnce(() => {
    if (!seeded) return;
    const reversed = [...initialMessages].reverse();
    const lastUser = reversed.find((m) => m.role === "user");
    const lastAssistant = reversed.find((m) => m.role === "assistant");
    if (lastUser !== undefined && lastAssistant !== undefined)
      followUps.refresh(uiMessageText(lastUser), uiMessageText(lastAssistant));
  });

  // The turn the server persisted while the client was disconnected, adopted as
  // history: the same content a reload would have shown, sources included.
  function adoptRecoveredTurn(messages: ConversationMessage[]): void {
    // Stop first. Usually a no-op (the transport already failed), but the
    // return-to-foreground check can land on a stream that still believes it is
    // running — and writing over the messages under a live reader would splice the
    // persisted answer and the deltas still arriving into one garbled turn.
    void chat.stop();
    const uiMessages = toUIMessages(messages);
    chat.setMessages(uiMessages);
    // No longer a failed turn — it is history now.
    chat.clearError();
    const reversed = [...uiMessages].reverse();
    const lastUser = reversed.find((message) => message.role === "user");
    const lastAssistant = reversed.find(
      (message) => message.role === "assistant"
    );
    if (lastUser !== undefined && lastAssistant !== undefined)
      followUps.refresh(uiMessageText(lastUser), uiMessageText(lastAssistant));
    // The list card's ordering and preview moved with the turn we just missed.
    void revalidate("/webapp/private/conversations");
    clearApiQuery("/webapp/private/conversations/latest-topic", {});
  }

  // The recovery closed its window on a turn that never landed. The chat may still
  // believe it is streaming — a socket the OS suspended dies in silence — and would
  // hold the composer locked forever; stop it, and let the failure surface
  // (`hasLostTurn`) take over.
  function stopLostTurn(): void {
    void chat.stop();
  }

  // Dispatch a turn to the blocking Evidence engine on THIS conversation, show a
  // working turn, then append the answered turn so it renders inline (graph via
  // the overlay). Shared by forced Evidence (`autoRouterPrediction: null`) and an
  // Auto→Evidence redispatch (carrying the classifier prediction so the turn is
  // persisted as the Auto turn it was — docs/gpt-mode-router.md).
  //
  // `placement` `replaceTrailingTurn` — the redispatch path. The general stream left an
  // answerless turn (the Auto turn it handed back); dropping it must happen when
  // the Evidence answer RESOLVES, not up front: the SDK finalizes that turn right
  // after its `onFinish` (where the redispatch fires), so an up-front `setMessages`
  // there is clobbered. Rebuilding at resolve time reads the settled transcript.
  function runEvidenceTurn(
    question: string,
    origin: QuestionOrigin,
    autoRouterPrediction: RouterPrediction | null,
    placement: BlockingTurnPlacement
  ): Promise<void> {
    // On a redispatch the general turn already shows the question — don't
    // repeat it in the working card.
    setPendingEvidence({
      question,
      renderQuestion: placement.kind === "append",
    });
    return evidence
      .send(question, origin, autoRouterPrediction)
      .then((result) => {
        setPendingEvidence(null);
        if (result === null) {
          displayToast(t("Something went wrong. Please try again."), "error");
          return;
        }
        const base = transcriptUnder(placement);
        chat.setMessages([
          ...base,
          {
            id: seedId("user", base.length),
            role: "user",
            parts: [{ type: "text", text: question }],
          },
          {
            id: seedId("assistant", base.length + 1),
            role: "assistant",
            parts: [],
          },
        ]);
        void revalidate("/webapp/private/conversations/:id", { param: { id } });
        void revalidate("/webapp/private/conversations");
        clearApiQuery("/webapp/private/conversations/latest-topic", {});
      });
  }

  // Dispatch a turn to the blocking Search engine (find_publication) on THIS
  // conversation: the endpoint extracts a search term, runs the same retrieval as
  // /search, and persists the turn; the answered turn is then appended so it
  // renders inline as result rows (the `searchByTurnIndex` overlay). Shared by
  // forced Search (`autoRouterPrediction: null`) and an Auto→Search redispatch
  // (carrying the classifier prediction so the turn is persisted as the Auto turn
  // it was — docs/gpt-mode-router.md).
  //
  // `placement` `replaceTrailingTurn` — the redispatch path, same contract as
  // `runEvidenceTurn`: the general stream left an answerless turn, and dropping
  // it must happen when the search RESOLVES, not up front (the SDK finalizes that
  // turn right after the `onFinish` the redispatch fires from, clobbering an
  // up-front `setMessages`).
  function runSearchTurn(
    question: string,
    origin: QuestionOrigin,
    autoRouterPrediction: RouterPrediction | null,
    placement: BlockingTurnPlacement
  ): Promise<void> {
    setPendingSearch({ question, renderQuestion: placement.kind === "append" });
    return apiClient
      .post("/webapp/private/conversations/:id/search", {
        param: { id },
        json: {
          question,
          language: activeLang,
          origin,
          autoRouterPrediction,
          includePubmed: isPubmedSectionEnabled,
          client: currentClient(),
        },
        // A blocking retrieval (MeSH resolve + search + card population) can
        // outrun the request layer's 60 s default on a slow connection.
        signal: AbortSignal.timeout(SEARCH_TURN_TIMEOUT_MS),
      })
      .then(({ body }) => {
        setPendingSearch(null);
        const base = transcriptUnder(placement);
        chat.setMessages([
          ...base,
          {
            id: seedId("user", base.length),
            role: "user",
            parts: [{ type: "text", text: question }],
          },
          {
            id: seedId("assistant", base.length + 1),
            role: "assistant",
            parts: [],
          },
        ]);
        setLiveSearchTurns((prev) => [
          ...prev,
          {
            turnIndex: body.turnIndex,
            framing: body.framing,
            docs: body.docs,
            pubmedDocs: body.pubmedDocs,
            refinements: body.refinements,
          },
        ]);
        void revalidate("/webapp/private/conversations/:id", { param: { id } });
        void revalidate("/webapp/private/conversations");
        clearApiQuery("/webapp/private/conversations/latest-topic", {});
      })
      .catch(() => {
        // Never swallow silently (webapp rule #5): a failed blocking search —
        // including an out-of-credits refusal from `guardGptCredits` — must tell
        // the user rather than just clearing the loader. (The rich budget UI the
        // streaming path shows via `GPTChatError` reads server budget state and
        // doesn't apply to this one-shot POST; a toast is the honest minimum.)
        setPendingSearch(null);
        displayToast(t("Something went wrong. Please try again."), "error");
      });
  }

  // What a blocking turn lands on. A forced turn appends to the screen `submit`
  // settled on: a lost turn it removed is still in this render's
  // `chat.messages`, so reading that snapshot would bring it back. A redispatch
  // reads the settled transcript at resolve time and drops this question's
  // answerless general turn, so the blocking turn lands at the turnIndex the
  // server persisted it under (its overlay key).
  function transcriptUnder(
    placement: BlockingTurnPlacement
  ): GeneralChatUIMessage[] {
    switch (placement.kind) {
      case "append":
        return placement.onto;
      case "replaceTrailingTurn": {
        const dropped = dropTurnToRetry(chat.messages);
        return dropped === null ? chat.messages : dropped.kept;
      }
      default:
        throw new Error(placement satisfies never);
    }
  }

  // Auto → Evidence / Search (docs/gpt-mode-router.md): the general stream handed
  // the turn back (data-router). Run the target engine for the same question —
  // labelled as the Auto turn it was, carrying the classifier prediction. The
  // answerless general turn is replaced when the answer resolves (the
  // `replaceTrailingTurn` placement).
  function redispatchToBlockingEngine(redispatch: RouterRedispatch): void {
    // The Auto turn armed stream recovery on the general send; it just handed off
    // to a blocking engine, so SETTLE recovery here — otherwise it treats the
    // answerless general turn as a dropped connection, times out into a
    // GPTChatError, and a retry then 409s against the blocking turn holding the
    // shared lock.
    recovery.onTurnSettled();
    void blockingDispatch.run(() => {
      // No `gaEventSearchGpt` here: this is the SAME question the general send
      // already fired for in `submit` — a second fire double-counts it. The
      // client event keeps `resolved_mode: "general"` for a redispatched Auto
      // turn; the DB `routerPrediction` is the resolution SoT
      // (docs/gpt-mode-router.md).
      switch (redispatch.resolvedMode) {
        case "evidence":
          return runEvidenceTurn(
            redispatch.question,
            redispatch.origin,
            redispatch.routerPrediction,
            { kind: "replaceTrailingTurn" }
          );
        case "find_publication":
          return runSearchTurn(
            redispatch.question,
            redispatch.origin,
            redispatch.routerPrediction,
            { kind: "replaceTrailingTurn" }
          );
        default:
          throw new Error(redispatch.resolvedMode satisfies never);
      }
    });
  }

  // `submittedMode` is passed explicitly (not read from the store) so a turn is
  // always sent in the mode chosen when it was submitted — the pending handoff
  // from the landing carries its own, which may differ from the live picker.
  // `attempt` is "retry" when the reader re-sends a question whose answer
  // failed: it is the same question, so it is not counted again in analytics
  // (the assistant engine's retry is silent too).
  function submit(
    question: string,
    origin: QuestionOrigin,
    submittedMode: GptMode,
    attempt: "new" | "retry"
  ): void {
    if (question === "") return;
    // Ignore re-entrant sends while a turn is in flight. The composer's own
    // `sendDisabled` covers typed sends, but the Search refinement chips and the
    // follow-up chips call `submit` directly, bypassing it — two blocking
    // Search/Evidence turns overlapping would append against a stale
    // `chat.messages` snapshot and cross their `turnIndex` overlays (cursor:
    // concurrent Search refine race).
    if (
      pendingSearch !== null ||
      pendingEvidence !== null ||
      chat.status === "submitted" ||
      chat.status === "streaming"
    )
      return;
    followUps.clear();
    // A turn given up on (refused, failed, or never found by the probes): put
    // the screen back to the questions the server holds before the next one,
    // so the `turnIndex` of every request counts them. If the turn lands after
    // all, the adoption effect reloads the transcript, which is longer. Every
    // path below builds on `screen`: this render's `chat.messages` is the
    // snapshot from before the `setMessages`.
    const lost = recovery.lostTurn;
    const { screen, endedWith } =
      lost === null
        ? { screen: chat.messages, endedWith: endingAnswerOf(chat.messages) }
        : afterLostTurn(chat.messages, lost);
    if (lost !== null) {
      chat.setMessages(screen);
      chat.clearError();
      recovery.onTurnSettled();
    }

    // Forced Evidence (docs/gpt-mode-router.md): dispatch straight to the Evidence
    // engine (no router prediction, nothing to replace — appends). Through the
    // single-flight so a double-send can't open two blocking turns.
    if (submittedMode === "evidence") {
      void blockingDispatch.run(() => {
        if (attempt === "new")
          gaEventSearchGpt({
            engine: "legacy",
            isNewConversation,
            origin,
            questionCount: questionCount + 1,
            question,
            selectedMode: "evidence",
            resolvedMode: "evidence",
            wasForced: true,
            classifierConfidence: null,
          });
        return runEvidenceTurn(question, origin, null, {
          kind: "append",
          onto: screen,
        });
      });
      return;
    }

    // Forced Search (find_publication): straight to the Search engine (no router
    // prediction, nothing to replace — appends). Through the single-flight so a
    // double-tapped refinement chip can't open two Search turns racing the
    // transcript append.
    if (submittedMode === "find_publication") {
      void blockingDispatch.run(() => {
        if (attempt === "new")
          gaEventSearchGpt({
            engine: "legacy",
            isNewConversation,
            origin,
            questionCount: questionCount + 1,
            question,
            selectedMode: "find_publication",
            resolvedMode: "find_publication",
            wasForced: true,
            classifierConfidence: null,
          });
        return runSearchTurn(question, origin, null, {
          kind: "append",
          onto: screen,
        });
      });
      return;
    }

    // Auto / General → the general streaming engine.
    // Half of what identifies the turn about to be sent (see `SentTurn`):
    // without it, the same question asked twice would recognise the PRE-turn
    // transcript as the recovered one.
    recovery.onTurnSent({
      kind: "ask",
      question,
      endedWith,
    });
    void chat.sendMessage(
      { text: question },
      { body: { origin, selectedMode: submittedMode } }
    );
    if (attempt === "new")
      gaEventSearchGpt({
        engine: "legacy",
        isNewConversation,
        origin,
        questionCount: questionCount + 1,
        question,
        selectedMode: submittedMode,
        // Shadow slice: Auto/General both dispatch to the general engine.
        resolvedMode: "general",
        wasForced: submittedMode !== "auto",
        // Client-side null this slice — the SSE route doesn't return the shadow
        // prediction yet (the DB `routerPrediction` is the eval SoT).
        classifierConfidence: null,
      });
  }

  // Regenerate the last turn of `screen`. A replay is a turn like any other,
  // and the reason `endedWith` exists: it re-asks the question the transcript
  // ALREADY ends with, so the answer being replaced — the one the SERVER ends
  // with — is the only thing that tells the recovered turn apart from it.
  function regenerateOn(
    screen: GeneralChatUIMessage[],
    endedWith: string | null
  ): void {
    const lastUser = screen.findLast((message) => message.role === "user");
    if (lastUser !== undefined)
      recovery.onTurnSent({
        kind: "regenerate",
        question: uiMessageText(lastUser),
        endedWith,
      });
    void chat.regenerate();
  }

  function retryAfterError(): void {
    const lost = recovery.lostTurn;
    if (lost !== null)
      switch (lost.kind) {
        case "ask":
          break;
        // A failed REGENERATE left the server's turn in place, its answer
        // included: retrying it is a regenerate again, or the question would
        // be appended a second time. The screen has already lost that answer,
        // so the landmark is the one the lost turn carried.
        case "regenerate": {
          const { screen, endedWith } = afterLostTurn(chat.messages, lost);
          regenerateOn(screen, endedWith);
          return;
        }
        default:
          throw new Error(lost.kind satisfies never);
      }
    const retry = dropTurnToRetry(chat.messages);
    if (retry === null) return;
    chat.setMessages(retry.kept);
    // The failed turn's original input method is not recoverable client-side;
    // a manual retry is the honest label.
    submit(retry.question, "typed", mode, "retry");
  }

  function handleCopyAnswer(
    question: string,
    text: string,
    answerForm: GptAnswerForm
  ): void {
    copyAnswer(question, text, () => {
      gaEventGptInteraction({
        engine: "legacy",
        isNewConversation,
        isSuggestedQuestion: false,
        questionCount,
        interaction: "copy_answer",
        answerForm,
      });
      displayToast(
        isDesktopShell() ? t("Answer copied") : t("Copied"),
        "success"
      );
    });
  }

  function comingSoon(): void {
    displayToast(t("Coming soon"), "info");
  }

  // Back leaves for the JuisciGPT landing with the history drawer REVEALED —
  // the drawer is the list this conversation was picked from, so it is what
  // "back" means here. Not `goBack`: whether the drawer is open lives in the
  // landing's component state, not in its history entry, so restoring that
  // entry would bring the landing back with the drawer shut. `replace`, not
  // `push`: the conversation is not somewhere the hardware back gesture should
  // return to, and it also covers the entries with nothing behind them — a
  // deep link, and the `?new=true` conversations that were opened by REPLACING
  // the landing entry that minted them.
  function handleBack(): void {
    history.replace(gptNewChatPageLink(), HISTORY_ARRIVAL_REVEALING);
  }

  // Deleting the conversation being read leaves nothing to read, so it lands
  // exactly where Back does: the landing, with the history revealed.
  async function handleDelete(): Promise<boolean> {
    const deleted = await deleteConversation(id);
    if (deleted === null) return false;
    handleBack();
    return true;
  }

  // Sharing rides the same gate as the per-turn Share button: off, there is
  // nothing to offer, on either surface.
  const shareThisConversation = isShareEnabled
    ? () => {
        void shareConversation(id, questionCount);
      }
    : null;

  // The owner's actions, Share included, in one menu: the same "…" in the
  // mobile header and in the desktop top bar.
  const conversationMenu = (
    <ConversationMenu
      title={title}
      onShare={shareThisConversation}
      onUnshare={
        isShareEnabled && isShared
          ? () => {
              void unshareConversation(id);
            }
          : null
      }
      onDebug={null}
      onRename={(nextTitle) => renameConversation(id, nextTitle)}
      onDelete={handleDelete}
      className={null}
    />
  );

  // Wide screens only (the mobile back lives in `ContentHeader`, which is
  // `md:hidden`). The drawer's burger joins it on a narrowed window of the
  // mobile shell; the desktop shell lists the conversations in its sidebar.
  const { askedFrom } = useLocationState(locationStateSchema);
  const desktopLeading = isDesktopShell() ? (
    <GptConversationBreadcrumb
      askedFrom={askedFrom}
      newChatLink={gptNewChatPageLink()}
      // Back lands on the JuisciGPT landing, which the first crumb names.
      onBackToGpt={handleBack}
      // Named server-side from the first question, so null until it has landed.
      current={title === null ? t("New conversation") : title}
    />
  ) : (
    <>
      <BackButton
        onClick={handleBack}
        className="rounded-full bg-white/80 px-3 py-1.5 shadow-[0px_2px_8px_rgba(0,0,0,0.08)] backdrop-blur-md hover:bg-white"
      >
        {t("Back")}
      </BackButton>
      <GPTHistoryButton
        onClick={
          historyReveal.isOpen ? historyReveal.close : historyReveal.open
        }
        className="ml-1"
      />
    </>
  );

  const customizeAction = useSingleFlightAction();

  async function finishCustomize(): Promise<void> {
    if (!customizing) return;
    await customizeAction.run(async () => {
      try {
        const { body } = await apiClient.put(
          "/webapp/private/users/me/preference-onboarding/complete",
          {}
        );
        const cachedUser = getCurrentUserData();
        if (cachedUser !== null) {
          replaceCurrentUserData({
            ...cachedUser,
            preferenceOnboarding: body.preferenceOnboarding,
          });
        }
        gaEventFeedTasteExplored({ flow: "preference_refresh" });
        resetPreferenceConversation();
        onCustomizingChange(false);
      } catch {
        displayToast(t("Something went wrong. Please try again."), "error");
      }
    });
  }

  if (customizing) {
    return (
      <div className="flex h-dvh flex-col bg-ds-cream">
        <ContentHeader
          transparent
          onBack={() => {
            onCustomizingChange(false);
          }}
        />
        <div className="relative min-h-0 flex-1">
          <ProfilingConversation
            headerInset={0}
            specialtyId={null}
            roomCount={currentUser === null ? 0 : currentUser.rooms.length}
            finishing={customizeAction.state.status === "running"}
            onFinish={() => {
              void finishCustomize();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <ScrollableLayout
        background={gptBackground()}
        headerClassName="bg-white/90 backdrop-blur-md"
        // No history burger in the mobile header: it carries the conversation's
        // own chrome (back, its title, start-a-new-chat), the same chrome
        // Chat-with-Paper shows. The drawer stays reachable — swipe right, or
        // Back, which lands on the JuisciGPT composer and its burger.
        // `desktopLeading` keeps its burger on a wide mobile-shell window, where
        // the header is `md:hidden` and there is no sidebar.
        header={
          <GptConversationHeader
            title={title}
            onBack={handleBack}
            // No grounding paper on the general surface — the posters are a
            // Chat-with-Paper affordance.
            visualAbstracts={null}
            newConversationTo={gptNewChatPageLink()}
            actions={conversationMenu}
          />
        }
        scrollRef={scrollRef}
        onScroll={onScroll}
        style={{
          ...historyReveal.scrollLockStyle,
          paddingBottom: keyboardPaddingBottom,
        }}
        hideScrollbar
      >
        <GptConversation
          chat={chat}
          leading={desktopLeading}
          headerActions={conversationMenu}
          composerNotice={
            band.showAcquisitionAsk ? <AcquisitionSuggestionCard /> : null
          }
          afterTurns={
            // The getting-started actions live in the conversation content while
            // the agent's opener is the only message — once the user replies,
            // the conversation behaves like any other.
            isOnboardingConversation && chat.messages.length === 1 ? (
              <OnboardingGettingStarted
                onCustomize={() => {
                  onCustomizingChange(true);
                }}
              />
            ) : null
          }
          cwyopPaper={null}
          // Ungated (docs/gpt-mode-router.md): the picker shows for everyone.
          showModePicker={true}
          evidenceGraphByTurnIndex={evidenceGraphByTurnIndex}
          evidenceCheckByRunId={new Map()}
          onOpenEvidenceSource={(source, citationNumber, quotes) => {
            gaEventGptInteraction({
              engine: "legacy",
              isNewConversation,
              isSuggestedQuestion: false,
              questionCount,
              interaction: "open_source",
              answerForm: "evidence_graph",
            });
            setOpenEvidenceSource({ source, citationNumber, quotes });
          }}
          pendingEvidence={pendingEvidence}
          searchByTurnIndex={searchByTurnIndex}
          pendingSearch={pendingSearch}
          onOpenSearchArticle={(article) => {
            history.push(articleLink(article));
          }}
          onRefineSearch={(refinement) => {
            submit(
              refinement,
              "followup_suggestion",
              "find_publication",
              "new"
            );
          }}
          onAskGeneral={(question) => {
            submit(question, "followup_suggestion", "general", "new");
          }}
          onAnswerCard={null}
          answerMath={false}
          actions={{
            onCopy: handleCopyAnswer,
            onShare: isShareEnabled
              ? () => {
                  void shareConversation(id, questionCount);
                }
              : comingSoon,
            showShare: isShareEnabled,
            onRegenerate: () => {
              regenerateOn(chat.messages, endingAnswerOf(chat.messages));
            },
            onRetry: retryAfterError,
            // Every reader can evaluate the turns of their own conversations.
            ...legacyEvaluation(id, myAnnotations),
            comingSoon,
          }}
          followUps={{
            enabled: true,
            questions: followUps.questions,
            onPick: (question) => {
              submit(question, "followup_suggestion", mode, "new");
            },
          }}
          disclaimer={t(
            "AI can make mistakes. Consider checking Source information."
          )}
          background={gptBackground()}
          roomId={roomId}
          assistantConversationId={null}
          viewStateKey={id}
          onOpenSources={(answerForm) => {
            gaEventGptInteraction({
              engine: "legacy",
              isNewConversation,
              isSuggestedQuestion: false,
              questionCount,
              interaction: "open_reference",
              answerForm,
            });
          }}
          onOpenSource={(answerForm) => {
            gaEventGptInteraction({
              engine: "legacy",
              isNewConversation,
              isSuggestedQuestion: false,
              questionCount,
              interaction: "open_source",
              answerForm,
            });
          }}
          isReconnecting={recovery.isReconnecting}
          hasLostTurn={recovery.isLost}
          // The drawer's search field owns the keyboard while it is open.
          followKeyboard={!historyReveal.isOpen}
          composer={{
            kind: "input",
            onSubmit: (question) => {
              submit(question, "typed", mode, "new");
            },
            onStop: () => {
              // Stop means stop waiting too: the server will still finish and
              // persist the turn (it shows up on the next visit), but the user
              // asked to have the composer back.
              recovery.onTurnSettled();
              void chat.stop();
            },
            placeholder: isDesktopShell()
              ? t("Describe a case, a decision, or a clinical doubt…")
              : t("Keep asking..."),
          }}
          scrollRef={scrollRef}
          isAtBottom={isAtBottom}
          onScrollToBottom={scrollToBottom}
          revealAssistantId={revealAssistantId}
          suggestion={band.suggestion}
          pushOptIn={band.showPushOptIn ? { onDone: () => {} } : null}
          attachments={attachments}
          // A live conversation: attaching and detaching are both available.
          canModifyAttachments
          references={references}
          study={null}
          canModifyReferences
        />
      </ScrollableLayout>
      {/* The citation sheet for an inline Evidence turn (docs/gpt-mode-router.md),
          opened from a citation badge inside EvidenceAnswer. */}
      <EvidenceCitationSheet
        source={openEvidenceSource === null ? null : openEvidenceSource.source}
        citationNumber={
          openEvidenceSource === null ? null : openEvidenceSource.citationNumber
        }
        quotes={openEvidenceSource === null ? [] : openEvidenceSource.quotes}
        roomId={roomId}
        isOpen={openEvidenceSource !== null}
        onClose={() => {
          setOpenEvidenceSource(null);
        }}
      />
    </>
  );
}
